import Head from 'next/head'
import { LOGO_URL, SITE_URL, productHref } from '@/lib/products'

// FAQ sections across the product pages use {question, answer}, {q, a} or {header, text}.
const normaliseFaqs = (faqs) =>
    (faqs || [])
        .map((f) => ({
            question: f.question || f.q || f.header,
            answer: f.answer || f.a || f.text,
        }))
        .filter((f) => f.question && f.answer)

/*
 * Per-page SEO for a product page.
 *
 * Why the `key` props: utils/DynamicHead runs in _app on every page and emits site-wide
 * og:* and twitter:* defaults. next/head only de-duplicates <meta> by `name` (or by an
 * explicit key), never by `property`, so a page's own og:title was previously emitted
 * alongside the global one and crawlers took the first in document order. With matching
 * keys here and in DynamicHead, the page's tag replaces the default.
 *
 * The JSON-LD is a plain <script> inside <Head>, so it is in the server-rendered HTML.
 * (next/script with the default strategy injects after hydration and never reaches a crawler.)
 *
 * `faqs` must be the same array the page's FAQ section renders, so the FAQPage markup can
 * never drift from the visible copy.
 *
 * This component owns <title> and <meta name="description"> too (lib/products.js is the
 * single source), so the tab title, the social card and the SERP title cannot diverge.
 */
export default function ProductSeo({ product, faqs, image, withTitle = true }) {
    const url = productHref(product)
    const ogImage = image || LOGO_URL
    const faqItems = normaliseFaqs(faqs)

    const graph = [
        {
            '@type': 'SoftwareApplication',
            '@id': `${url}#software`,
            name: product.name,
            description: product.description,
            url,
            applicationCategory: product.category,
            operatingSystem: 'Web',
            image: ogImage,
            provider: { '@type': 'Organization', name: 'Cloudastra Technologies', url: SITE_URL },
        },
        {
            '@type': 'BreadcrumbList',
            '@id': `${url}#breadcrumb`,
            itemListElement: [
                { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
                { '@type': 'ListItem', position: 2, name: 'Products', item: `${SITE_URL}/products` },
                { '@type': 'ListItem', position: 3, name: product.name, item: url },
            ],
        },
    ]

    if (faqItems.length > 0) {
        graph.push({
            '@type': 'FAQPage',
            '@id': `${url}#faq`,
            mainEntity: faqItems.map((f) => ({
                '@type': 'Question',
                name: f.question,
                acceptedAnswer: { '@type': 'Answer', text: f.answer },
            })),
        })
    }

    const schema = { '@context': 'https://schema.org', '@graph': graph }
    const title = product.title || `${product.name} | Cloudastra Technologies`

    return (
        <Head>
            {withTitle && <title>{title}</title>}
            {withTitle && <meta name='description' content={product.description} />}
            <link key='canonical' rel='canonical' href={url} />
            <meta key='og:title' property='og:title' content={title} />
            <meta key='og:description' property='og:description' content={product.description} />
            <meta key='og:url' property='og:url' content={url} />
            <meta key='og:type' property='og:type' content='website' />
            <meta key='og:image' property='og:image' content={ogImage} />
            <meta key='og:image:alt' property='og:image:alt' content={product.name} />
            <meta key='twitter:card' name='twitter:card' content='summary_large_image' />
            <meta key='twitter:title' name='twitter:title' content={title} />
            <meta
                key='twitter:description'
                name='twitter:description'
                content={product.description}
            />
            <meta key='twitter:image' name='twitter:image' content={ogImage} />
            <meta key='twitter:url' name='twitter:url' content={url} />
            <script
                type='application/ld+json'
                dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
            />
        </Head>
    )
}
